import React, { useEffect, useState } from "react";
import ToolShell from "@/components/ToolShell";
import { track } from "@/lib/analytics";
import { Mail, AlertTriangle, Clock, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urgency(days) {
  if (days === null || days === undefined) return "normal";
  if (days < 0) return "passed";
  if (days <= 30) return "urgent";
  if (days <= 60) return "warning";
  return "normal";
}

export default function DeadlinesPage() {
  const [deadlines, setDeadlines] = useState([]);
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    track.tool_opened("deadlines");
    fetch(`${API}/state-deadlines`).then(r => r.json()).then(d => setDeadlines(d.deadlines || []));
    const params = new URLSearchParams(window.location.search);
    if (params.get("confirmed") === "ok") {
      setMsg({ kind: "ok", text: "Confirmed! We'll email you at 30, 14, 7, and 1 day before your state's deadline." });
      track.reminder_confirm();
    } else if (params.get("confirmed") === "invalid") {
      setMsg({ kind: "error", text: "That confirmation link was invalid or expired. Sign up again below." });
    } else if (params.get("unsubscribed") === "ok") {
      setMsg({ kind: "ok", text: "Unsubscribed. You won't hear from us again." });
      track.reminder_unsubscribe();
    }
  }, []);

  const selected = deadlines.find(d => d.code === state);
  const daysSel = selected ? Math.ceil((new Date(selected.fafsaDeadline).getTime() - Date.now()) / 86400000) : null;

  const subscribe = async (e) => {
    e.preventDefault();
    if (!state || !email) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, state }),
      });
      if (res.ok) {
        track.reminder_subscribe();
        setMsg({ kind: "ok", text: "Check your inbox, we just sent a confirmation link." });
        setEmail("");
      } else {
        const err = await res.json().catch(() => ({ detail: "Something went wrong." }));
        setMsg({ kind: "error", text: err.detail || "Something went wrong." });
      }
    } finally { setBusy(false); }
  };

  return (
    <ToolShell tool="Tool 02 · Deadline Tracker" title="Every state's FAFSA deadline." subtitle="State deadlines control state grants. Missing yours doesn't cost you federal aid, but state grants that run out don't come back.">
      {msg && (
        <div className={`card mb-6 flex items-start gap-3 ${msg.kind === "ok" ? "border-success" : "border-danger"}`} data-testid="deadline-msg">
          <CheckCircle2 className={`w-5 h-5 mt-0.5 ${msg.kind === "ok" ? "text-success" : "text-danger"}`} />
          <span className="text-sm">{msg.text}</span>
        </div>
      )}

      {/* Lookup */}
      <div className="card mb-8" data-testid="deadline-lookup">
        <label className="block text-sm text-ink-muted mb-2">Pick your state</label>
          <select className="select-clean max-w-md" value={state} data-testid="deadline-state-select"
          onChange={e => { setState(e.target.value); if (e.target.value) track.deadline_state_selected(); }}>
          <option value="">Select a state…</option>
          {deadlines.map(d => <option key={d.code} value={d.code}>{d.state}</option>)}
        </select>
        {selected && (
          <div className="mt-6 grid sm:grid-cols-3 gap-5" data-testid="deadline-result">
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-1">Deadline</div>
              <div className="font-heading text-3xl text-ink">{new Date(selected.fafsaDeadline + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-1">Days away</div>
              <div className={`font-heading text-3xl urgency-${urgency(daysSel)}`}>
                {daysSel < 0 ? `${Math.abs(daysSel)} days ago` : `${daysSel} day${daysSel === 1 ? "" : "s"}`}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-1">State aid program</div>
              <a href={selected.url} target="_blank" rel="noopener noreferrer" className="link text-sm">{selected.notes} <ExternalLink className="w-3 h-3 inline" /></a>
            </div>
          </div>
        )}
      </div>

      {/* Reminder opt-in */}
      {selected && daysSel >= 0 && (
        <div className="card mb-10" data-testid="deadline-reminder-card">
          <div className="flex items-start gap-3 mb-4">
            <Mail className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <div className="font-heading text-2xl text-ink">Get reminded before it's too late</div>
              <p className="text-sm text-ink-muted mt-1">We'll email at 30, 14, 7, and 1 day before {selected.state}'s deadline. Double opt-in. One-click unsubscribe. This is the only email AidWaive will ever send you.</p>
            </div>
          </div>
          <form onSubmit={subscribe} className="flex gap-3 flex-col sm:flex-row">
            <input type="email" required placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="input flex-1" data-testid="deadline-email-input" />
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="deadline-subscribe">
              {busy ? "Sending…" : "Send me reminders"}
            </button>
          </form>
        </div>
      )}

      {/* All states table */}
      <div>
        <h2 className="font-heading text-3xl mb-4 text-ink">All 50 states + DC</h2>
        <div className="card-tight overflow-x-auto">
          <table className="w-full text-sm" data-testid="deadlines-table">
            <thead>
              <tr className="text-left text-ink-dim border-b border-border">
                <th className="py-3 pr-4">State</th>
                <th className="py-3 pr-4">Deadline</th>
                <th className="py-3 pr-4">Days</th>
                <th className="py-3">Program</th>
              </tr>
            </thead>
            <tbody>
              {deadlines.map(d => {
                const days = Math.ceil((new Date(d.fafsaDeadline).getTime() - Date.now()) / 86400000);
                const u = urgency(days);
                return (
                  <tr key={d.code} className="border-b border-border last:border-0">
                    <td className="py-3 pr-4 text-ink">{d.state}</td>
                    <td className="py-3 pr-4 text-ink-muted">{new Date(d.fafsaDeadline + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                    <td className={`py-3 pr-4 urgency-${u}`}>{days < 0 ? "passed" : days}</td>
                    <td className="py-3 text-ink-muted text-xs">{d.notes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-md border border-amber/30 bg-amber/10 text-sm text-amber flex items-start gap-2" data-testid="deadline-note">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Federal FAFSA deadline is June 30 of the aid year. Most state grants have earlier priority deadlines, this table shows your state's priority deadline.</span>
      </div>
    </ToolShell>
  );
}
